import { AppConfig } from "@src/config/AppConfig";
import { AuthUserTokenService } from "@src/infrastructure/auth/AuthUserTokenService";
import { PrivateHttp } from "@src/infrastructure/adapters/http";

export class MultipartFormService extends PrivateHttp {
  async post<T>(path: string, formData: FormData): Promise<T> {
    this.appendFormRequestMetadata(formData);

    const response = await fetch(this.resolveUrl(path), {
      method: "POST",
      headers: await this.getMultipartAuthHeaders(),
      body: formData,
    });

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      throw new Error(response.statusText || "Request failed");
    }

    if (!response.ok) {
      const message =
        body != null &&
        typeof body === "object" &&
        !Array.isArray(body) &&
        typeof (body as { message?: unknown }).message === "string"
          ? (body as { message: string }).message
          : response.statusText || "Request failed";
      throw new Error(message);
    }

    if (
      body != null &&
      typeof body === "object" &&
      !Array.isArray(body) &&
      (body as Record<string, unknown>).status === "error"
    ) {
      const message =
        typeof (body as { message?: unknown }).message === "string"
          ? (body as { message: string }).message
          : "Request failed";
      throw new Error(message);
    }

    return body as T;
  }

  private async getMultipartAuthHeaders(): Promise<Record<string, string>> {
    const auth = new AuthUserTokenService();
    const [userAccessToken, adminAccessToken, adminRefreshToken] =
      await Promise.all([
        auth.getAuthAccessToken(),
        auth.getAdminAccessToken(),
        auth.getAdminRefreshToken(),
      ]);

    return {
      Authorization: `Bearer ${userAccessToken}`,
      ["X-Admin-Authorization"]: `Bearer ${adminAccessToken}`,
      ["X-Admin-Refresh-Token"]: `Bearer ${adminRefreshToken}`,
    };
  }

  private resolveUrl(path: string): string {
    const baseURL = AppConfig.getApiUrl().replace(/\/$/, "");
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${baseURL}${normalizedPath}`;
  }

  private appendFormRequestMetadata(formData: FormData): void {
    formData.append("timestamp", Date.now().toString());
    formData.append(
      "timezone",
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    );
  }
}
